import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { pb } from "../../../helpers/pocketbase_helper";
import TableContainer from "../../../components/Common/DataTableContainer";

const CentroCostos = () => {
    document.title = "Centros de Costo | GraphicForce - Printesco";

    const [centrosCosto, setCentrosCosto] = useState([]);
    const [centrosCostoInactivos, setCentrosCostoInactivos] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [form, setForm] = useState({ id: null, nombre: "", maquina: "", encargado: "", proceso: "", operario: "", proceso_proveedor: "" });
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [centroCostoToDelete, setCentroCostoToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [centroCostoToRestore, setCentroCostoToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Estados para las relaciones
    const [maquinas, setMaquinas] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [procesos, setProcesos] = useState([]);
    const [operarios, setOperarios] = useState([]);
    const [proveedores, setProveedores] = useState([]);

    // Fetch centros de costo con relaciones expandidas
    const fetchCentrosCosto = async () => {
        try {
            // Obtener todos los centros de costo con relaciones expandidas
            const resultList = await pb.collection("centro_costo").getList(1, 1000, {
                sort: "-created",
                expand: "maquina,encargado,proceso,operario,proceso_proveedor"
            });
            
            // Separar centros de costo activos e inactivos
            const activos = resultList.items.filter(centro => centro.activo !== false);
            const inactivos = resultList.items.filter(centro => centro.activo === false);
            
            // Aplicar paginación solo a los activos
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activosPaginados = activos.slice(startIndex, endIndex);
            
            setCentrosCosto(activosPaginados);
            setCentrosCostoInactivos(inactivos);
            setTotalRecords(activos.length);
        } catch (err) {
            console.error("Error fetching centros de costo:", err);
        }
    };

    // Fetch máquinas disponibles
    const fetchMaquinas = async () => {
        try {
            const resultList = await pb.collection("maquina").getList(1, 1000, {
                filter: "activo = true",
                sort: "nombre"
            });
            setMaquinas(resultList.items);
        } catch (err) {
            console.error("Error fetching máquinas:", err);
        }
    };

    // Fetch usuarios y operarios en una sola llamada para evitar auto-cancelación
    const fetchUsuariosYOperarios = async () => {
        try {
            // Obtener solo usuarios activos con expansión de rol
            const resultList = await pb.collection("users").getList(1, 1000, {
                filter: "activo = true",
                expand: "rol",
                sort: "name"
            });
            
            // Separar usuarios y operarios (solo activos)
            const todosUsuarios = resultList.items;
            const operarios = todosUsuarios.filter(user => 
                user.expand?.rol?.nombre?.toLowerCase() === 'operario'
            );
            
            setUsuarios(todosUsuarios);
            setOperarios(operarios);
        } catch (err) {
            console.error("Error cargando usuarios y operarios:", err);
        }
    };

    // Fetch procesos disponibles
    const fetchProcesos = async () => {
        try {
            const resultList = await pb.collection("mapa_nombre_proceso_tabla").getList(1, 1000, {
                sort: "nombre"
            });
            setProcesos(resultList.items);
        } catch (err) {
            console.error("Error fetching procesos:", err);
        }
    };

    // Fetch proveedores de proceso disponibles
    const fetchProveedores = async () => {
        try {
            const resultList = await pb.collection("proceso_proveedor").getList(1, 1000, {
                filter: "activo = true",
                sort: "nombre"
            });
            setProveedores(resultList.items);
        } catch (err) {
            console.error("Error fetching proveedores:", err);
        }
    };

    useEffect(() => {
        fetchCentrosCosto();
        fetchMaquinas();
        fetchUsuariosYOperarios();
        fetchProcesos();
        fetchProveedores();
    }, [pageIndex, pageSize]);

    const reloadCentrosCosto = () => {
        fetchCentrosCosto();
        fetchUsuariosYOperarios();
    };

    // Handlers

    const handleEdit = (centroCosto) => {
        setForm({ 
            id: centroCosto.id,
            nombre: centroCosto.nombre, 
            maquina: centroCosto.maquina || "", 
            encargado: centroCosto.encargado || "",
            proceso: centroCosto.proceso || "",
            operario: centroCosto.operario || "",
            proceso_proveedor: centroCosto.proceso_proveedor || ""
        });
        setModalError("");
        setModalAdd(true);
    };

    const handleRemove = (centroCosto) => {
        setCentroCostoToDelete(centroCosto);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!centroCostoToDelete) return;
        setDeleting(true);
        try {
            await pb.collection("centro_costo").update(centroCostoToDelete.id, { activo: false });
            fetchCentrosCosto();
            setShowDeleteAlert(false);
            setCentroCostoToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar centro de costo");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setCentroCostoToDelete(null);
    };

    // Show alert and store centro de costo to restore
    const handleRestore = (centroCosto) => {
        setCentroCostoToRestore(centroCosto);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    // Confirm restore: set activo to true
    const confirmRestore = async () => {
        if (!centroCostoToRestore) return;
        setRestoring(true);
        try {
            await pb.collection("centro_costo").update(centroCostoToRestore.id, { activo: true });
            fetchCentrosCosto();
            setShowRestoreAlert(false);
            setCentroCostoToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar centro de costo");
        }
        setRestoring(false);
    };

    // Cancel restore
    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setCentroCostoToRestore(null);
    };

    const handleAdd = () => {
        setForm({ id: null, nombre: "", maquina: "", encargado: "", proceso: "", operario: "", proceso_proveedor: "" });
        setModalAdd(true);
    };

    const handleChange = e => {
        const { name, value } = e.target;
        if (name === "nombre") {
            setForm({ ...form, [name]: value.toUpperCase() });
        } else {
            setForm({ ...form, [name]: value });
        }
    };

    const handleSave = async e => {
        e.preventDefault();
        setSaving(true);
        setModalError("");
        
        // Validación del campo proceso
        if (!form.proceso) {
            setModalError("El campo Proceso es obligatorio");
            setSaving(false);
            return;
        }
        
        try {
            const data = {
                nombre: form.nombre,
                maquina: form.maquina || null,
                encargado: form.encargado || null,
                proceso: form.proceso,
                operario: form.operario || null,
                proceso_proveedor: form.proceso_proveedor || null,
                activo: true,
            };
            
            if (form.id) {
                // Actualizar centro de costo existente
                await pb.collection("centro_costo").update(form.id, data);
            } else {
                // Crear nuevo centro de costo
                await pb.collection("centro_costo").create(data);
            }
            
            fetchCentrosCosto();
            setModalAdd(false);
        } catch (err) {
            if (
                err?.data?.data?.nombre &&
                err.data.data.nombre.code === "validation_not_unique"
            ) {
                setModalError(
                    `El centro de costo con el nombre "${form.nombre}" ya existe, por favor valide la información e intente de nuevo`
                );
            } else {
                setModalError(form.id ? "Error al actualizar centro de costo" : "Error al agregar centro de costo");
            }
        }
        setSaving(false);
    };

    // Helper function to get machine name
    const getMachineName = (maquinaId) => {
        if (!maquinaId) return "-";
        const maquina = maquinas.find(m => m.id === maquinaId);
        return maquina ? maquina.nombre : "Máquina no encontrada";
    };

    // Helper function to get user name
    const getUserName = (userId) => {
        if (!userId) return "Sin asignar";
        const usuario = usuarios.find(u => u.id === userId);
        return usuario ? usuario.name : "Sin asignar";
    };

    // Helper function to get process name
    const getProcessName = (procesoId) => {
        if (!procesoId) return "-";
        const proceso = procesos.find(p => p.id === procesoId);
        return proceso ? proceso.nombre : "Proceso no encontrado";
    };

    // Helper function to get operario name
    const getOperarioName = (operarioId, centroCosto = null) => {
        if (!operarioId) return "Sin asignar";
        
        // First try to get from expanded data if available
        if (centroCosto && centroCosto.expand && centroCosto.expand.operario) {
            return centroCosto.expand.operario.name || "Sin asignar";
        }
        
        // Fallback to operarios list
        const operario = operarios.find(o => o.id === operarioId);
        return operario ? operario.name : "Sin asignar";
    };

    // Helper function to get proveedor name
    const getProveedorName = (proveedorId, centroCosto = null) => {
        if (!proveedorId) return "Sin asignar";
        
        // First try to get from expanded data if available
        if (centroCosto && centroCosto.expand && centroCosto.expand.proceso_proveedor) {
            return centroCosto.expand.proceso_proveedor.nombre || "Sin asignar";
        }
        
        // Fallback to proveedores list
        const proveedor = proveedores.find(p => p.id === proveedorId);
        return proveedor ? proveedor.nombre : "Sin asignar";
    };

    // --- Filtering and Pagination ---
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Máquina",
            accessorKey: "maquina",
            cell: info => getMachineName(info.getValue()),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Encargado",
            accessorKey: "encargado",
            cell: info => getUserName(info.getValue()),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Proceso",
            accessorKey: "proceso",
            cell: info => getProcessName(info.getValue()),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Operario",
            accessorKey: "operario",
            cell: info => getOperarioName(info.getValue(), info.row.original),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Proveedor",
            accessorKey: "proceso_proveedor",
            cell: info => getProveedorName(info.getValue(), info.row.original),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const centroCosto = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="warning" size="sm" className="me-2" onClick={() => handleEdit(centroCosto)} disabled={centroCosto.activo === false}>
                            <i className="fas fa-pen me-1"></i> Editar
                        </Button>
                        <Button color="danger" size="sm" onClick={() => handleRemove(centroCosto)} disabled={centroCosto.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [centrosCosto, maquinas, usuarios, procesos, operarios, proveedores]);

    // Columnas para centros de costo inactivos
    const columnsInactivos = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Máquina",
            accessorKey: "maquina",
            cell: info => getMachineName(info.getValue()),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Encargado",
            accessorKey: "encargado",
            cell: info => getUserName(info.getValue()),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Proceso",
            accessorKey: "proceso",
            cell: info => getProcessName(info.getValue()),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Operario",
            accessorKey: "operario",
            cell: info => getOperarioName(info.getValue(), info.row.original),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Proveedor",
            accessorKey: "proceso_proveedor",
            cell: info => getProveedorName(info.getValue(), info.row.original),
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const centroCosto = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(centroCosto)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [centrosCostoInactivos, maquinas, usuarios, procesos, operarios, proveedores]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Centro de Costos" breadcrumbItem="Agregar/Editar" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && centroCostoToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará el centro de costo <b>{centroCostoToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && centroCostoToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto restaurará el centro de costo <b>{centroCostoToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Centros de Costo
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadCentrosCosto}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                                        <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                                            <TableContainer
                                                columns={columns}
                                                data={centrosCosto}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                    {/* Tabla de Centros de Costo Inactivos */}
                    <Row className="mt-4">
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Centros de Costo Inactivos
                                        <div>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                title="Recargar tabla"
                                                onClick={reloadCentrosCosto}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin" style={{ overflow: "visible" }}>
                                        <div className="table-responsive mb-0" data-pattern="priority-columns" style={{ overflow: "visible" }}>
                                            <TableContainer
                                                columns={columnsInactivos}
                                                data={centrosCostoInactivos}
                                                isGlobalFilter={false}
                                                isPagination={false}
                                                isCustomPageSize={false}
                                                tableClass="table-bordered table-nowrap dt-responsive w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </div>
            </div>

            <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                <ModalHeader toggle={() => setModalAdd(!modalAdd)}>
                    {form.id ? "Editar Centro de Costo" : "Agregar Centro de Costo"}
                </ModalHeader>
                <Form onSubmit={handleSave}>
                    <ModalBody>
                        {modalError && (
                            <Alert
                                color="danger"
                                isOpen={!!modalError}
                                toggle={() => setModalError("")}
                                fade={true}
                                transition={{ timeout: 150 }}
                                className="d-flex align-items-center justify-content-between mb-3"
                            >
                                <div>
                                    <strong>Error:</strong> {modalError}
                                </div>
                            </Alert>
                        )}
                        <FormGroup>
                            <Label for="nombre">Nombre</Label>
                            <Input
                                type="text"
                                name="nombre"
                                id="nombre"
                                value={form.nombre}
                                onChange={handleChange}
                                placeholder="Ingrese el nombre del centro de costo"
                                required
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="maquina">Máquina (Opcional)</Label>
                            <Input
                                type="select"
                                name="maquina"
                                id="maquina"
                                value={form.maquina}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione una máquina</option>
                                {maquinas.map(maquina => (
                                    <option key={maquina.id} value={maquina.id}>
                                        {maquina.nombre}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="encargado">Encargado (Opcional)</Label>
                            <Input
                                type="select"
                                name="encargado"
                                id="encargado"
                                value={form.encargado}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione un encargado</option>
                                {usuarios.map(usuario => (
                                    <option key={usuario.id} value={usuario.id}>
                                        {usuario.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="proceso">Proceso *</Label>
                            <Input
                                type="select"
                                name="proceso"
                                id="proceso"
                                value={form.proceso}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Seleccione un proceso</option>
                                {procesos.map(proceso => (
                                    <option key={proceso.id} value={proceso.id}>
                                        {proceso.nombre}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="operario">Operario (Opcional)</Label>
                            <Input
                                type="select"
                                name="operario"
                                id="operario"
                                value={form.operario}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione un operario</option>
                                {operarios.map(operario => (
                                    <option key={operario.id} value={operario.id}>
                                        {operario.name}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label for="proceso_proveedor">Proveedor de Proceso (Opcional)</Label>
                            <Input
                                type="select"
                                name="proceso_proveedor"
                                id="proceso_proveedor"
                                value={form.proceso_proveedor}
                                onChange={handleChange}
                            >
                                <option value="">Seleccione un proveedor</option>
                                {proveedores.map(proveedor => (
                                    <option key={proveedor.id} value={proveedor.id}>
                                        {proveedor.nombre}
                                    </option>
                                ))}
                            </Input>
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModalAdd(false)}>
                            Cancelar
                        </Button>
                        <Button color="primary" type="submit" disabled={saving}>
                            {saving ? <Spinner size="sm" className="me-2" /> : null}
                            {saving ? "Guardando..." : (form.id ? "Actualizar" : "Guardar")}
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </React.Fragment>
    );
};

export default CentroCostos;
